import { Link, useParams } from "@tanstack/react-router"
import { ArrowUpRight, Boxes } from "lucide-react"
import { useMemo } from "react"
import { PageHeader } from "@/components/shell/page-header"
import { EmptyState, ErrorState } from "@/components/states"
import { Screen } from "@/components/ui/screen"
import { Skeleton } from "@/components/ui/skeleton"
import type { Scope } from "@/lib/connection"
import { useT } from "@/lib/i18n"
import { useScope } from "@/lib/scope"
import { Diagram, type DiagramEdge, type DiagramNode } from "./diagram"
import { useOntology } from "./hooks"
import { HowItWorks } from "./how-it-works"
import type { AreaMeta, Ontology, TypeMeta } from "./ontology"

/** Describe a type for people: a Core description when declared, else where it was imported from. */
function typeBlurb(type: TypeMeta): string | undefined {
  if (type.description && !/^Technical view of /.test(type.description)) return type.description
  return type.module
}

function AreaCard({ scope, area, ontology }: { scope: Scope; area: AreaMeta; ontology: Ontology }) {
  const t = useT()
  const sample = area.types
    .map((n) => ontology.types.get(n))
    .filter((x): x is TypeMeta => Boolean(x))
    .sort((a, b) => (ontology.incoming.get(b.name)?.length ?? 0) + b.links.length - ((ontology.incoming.get(a.name)?.length ?? 0) + a.links.length))
    .slice(0, 3)
  return (
    <Link
      to="/$orgId/$environmentId/areas/$areaId"
      params={{ ...scope, areaId: area.id }}
      className="group flex h-full flex-col justify-between rounded-2xl border border-border bg-card px-4 py-3 outline-none transition-[border-color,background-color] duration-150 hover:border-foreground/25 hover:bg-accent/40 focus-visible:border-ring focus-visible:ring-3 focus-visible:ring-ring/30"
    >
      <div className="flex items-center gap-2">
        <span className="truncate text-[15px] font-semibold tracking-tight">{area.label}</span>
        {area.core ? <span className="rounded-full border border-border px-1.5 text-2xs text-muted-foreground">{t("ws.areas.core")}</span> : null}
        <ArrowUpRight className="ms-auto size-3.5 text-subtle-foreground opacity-0 transition-opacity group-hover:opacity-100 rtl:-scale-x-100" aria-hidden />
      </div>
      <p className="truncate text-xs text-muted-foreground">{sample.map((s) => s.pluralLabel).join(" · ")}</p>
      <p className="text-2xs text-subtle-foreground tabular-nums">{t("ws.areas.types", { count: area.types.length })}</p>
    </Link>
  )
}

/** The map of business areas and the typed links between them. */
export function AreaMap({ scope, ontology, compact = false }: { scope: Scope; ontology: Ontology; compact?: boolean }) {
  const t = useT()
  const nodes: DiagramNode[] = ontology.areas.map((area) => ({
    id: area.id,
    width: compact ? 176 : 212,
    height: compact ? 84 : 96,
    render: <AreaCard scope={scope} area={area} ontology={ontology} />,
  }))
  const edges: DiagramEdge[] = ontology.relations.map((r) => {
    const targets = [...new Set(r.links.map((l) => ontology.types.get(l.target)?.label ?? l.target))]
    return { id: `${r.from}>${r.to}`, from: r.from, to: r.to, label: undefined, tone: "strong", title: targets.join(", ") }
  })
  return <Diagram id={compact ? "area-map-compact" : "area-map"} nodes={nodes} edges={edges} label={t("ws.areas.mapLabel")} />
}

export function AreasPage() {
  const t = useT()
  const scope = useScope()
  const { ontology, defs } = useOntology(scope)
  return (
    <>
      <PageHeader
        crumbs={[
          <Link key="home" to="/$orgId/$environmentId" params={scope}>
            {t("ws.nav.home")}
          </Link>,
          t("ws.nav.areas"),
        ]}
        actions={<HowItWorks lines={["ws.how.areas"]} />}
      />
      <div className="min-h-0 flex-1 overflow-y-auto">
        <Screen width="board" className="space-y-8 px-4 py-8 md:px-8">
          <header className="space-y-1">
            <h1 className="text-[1.75rem] leading-tight font-semibold tracking-[-0.02em]">{t("ws.areas.title")}</h1>
            <p className="max-w-2xl text-sm text-muted-foreground">{t("ws.areas.description")}</p>
          </header>
          {defs.isError ? (
            <ErrorState error={defs.error} what={t("ws.areas.title")} onRetry={() => void defs.refetch()} />
          ) : !ontology ? (
            <Skeleton className="h-96 w-full rounded-2xl" />
          ) : ontology.areas.length === 0 ? (
            <EmptyState icon={<Boxes />} title={t("ws.areas.none")} />
          ) : (
            <>
              <div className="rounded-3xl border border-border bg-surface-subtle/40 p-4 md:p-6">
                <AreaMap scope={scope} ontology={ontology} />
              </div>
              <ul className="grid gap-x-8 gap-y-1 sm:grid-cols-2">
                {ontology.relations.flatMap((r) =>
                  r.links.slice(0, 4).map((l) => {
                    const from = ontology.types.get(l.source)
                    const to = ontology.types.get(l.target)
                    return (
                      <li key={`${l.source}.${l.id}`} className="flex items-baseline gap-2 border-b border-border py-2 text-sm">
                        <Link to="/$orgId/$environmentId/records/$objectType" params={{ ...scope, objectType: l.source }} className="hover:underline">
                          {from?.label ?? l.source}
                        </Link>
                        <span className="text-xs text-subtle-foreground">{l.label.toLowerCase()} →</span>
                        <Link to="/$orgId/$environmentId/records/$objectType" params={{ ...scope, objectType: l.target }} className="hover:underline">
                          {to?.label ?? l.target}
                        </Link>
                        <span className="ms-auto text-2xs text-subtle-foreground">
                          {ontology.areas.find((a) => a.id === r.from)?.label} → {ontology.areas.find((a) => a.id === r.to)?.label}
                        </span>
                      </li>
                    )
                  }),
                )}
              </ul>
            </>
          )}
        </Screen>
      </div>
    </>
  )
}

const DIAGRAM_LIMIT = 24

export function AreaPage() {
  const t = useT()
  const scope = useScope()
  const { areaId } = useParams({ from: "/$orgId/$environmentId/areas/$areaId" })
  const { ontology, defs } = useOntology(scope)
  const area = ontology?.areas.find((a) => a.id === areaId)
  const types = useMemo(() => (area && ontology ? area.types.map((n) => ontology.types.get(n)).filter((x): x is TypeMeta => Boolean(x)) : []), [area, ontology])
  const inArea = new Set(area?.types ?? [])
  const degree = (ty: TypeMeta) => ty.links.filter((l) => inArea.has(l.target)).length + (ontology?.incoming.get(ty.name) ?? []).filter((l) => inArea.has(l.source)).length
  const connected = types.filter((ty) => degree(ty) > 0).sort((a, b) => degree(b) - degree(a)).slice(0, DIAGRAM_LIMIT)
  const shown = new Set(connected.map((c) => c.name))
  const nodes: DiagramNode[] = connected.map((ty) => ({
    id: ty.name,
    width: 168,
    height: 52,
    render: (
      <Link
        to="/$orgId/$environmentId/records/$objectType"
        params={{ ...scope, objectType: ty.name }}
        className="flex h-full flex-col justify-center rounded-xl border border-border bg-card px-3 outline-none transition-colors hover:border-foreground/30 hover:bg-accent/40 focus-visible:border-ring focus-visible:ring-3 focus-visible:ring-ring/30"
      >
        <span className="truncate text-[13px] font-medium">{ty.pluralLabel}</span>
        {ty.lifecycle ? <span className="truncate text-2xs text-subtle-foreground">{t("ws.areas.lifecycleStates", { count: ty.lifecycle.states.length })}</span> : null}
      </Link>
    ),
  }))
  // Parent → child, so a chain reads in the order business happens (Order → Delivery → Invoice → Payment).
  const edges: DiagramEdge[] = connected.flatMap((ty) => ty.links.filter((l) => shown.has(l.target) && l.target !== ty.name).map((l) => ({ id: `${l.target}>${ty.name}.${l.id}`, from: l.target, to: ty.name })))
  const outward = types.flatMap((ty) => [
    ...ty.links.filter((l) => !inArea.has(l.target)).map((l) => ({ from: ty, link: l, other: l.target })),
    ...(ontology?.incoming.get(ty.name) ?? []).filter((l) => !inArea.has(l.source)).map((l) => ({ from: ty, link: l, other: l.source })),
  ])

  return (
    <>
      <PageHeader
        crumbs={[
          <Link key="home" to="/$orgId/$environmentId" params={scope}>
            {t("ws.nav.home")}
          </Link>,
          <Link key="areas" to="/$orgId/$environmentId/areas" params={scope}>
            {t("ws.nav.areas")}
          </Link>,
          area?.label ?? areaId,
        ]}
        actions={<HowItWorks lines={["ws.how.areas"]} />}
      />
      <div className="min-h-0 flex-1 overflow-y-auto">
        <Screen width="board" className="space-y-8 px-4 py-8 md:px-8">
          {defs.isError ? (
            <ErrorState error={defs.error} what={areaId} onRetry={() => void defs.refetch()} />
          ) : !ontology ? (
            <Skeleton className="h-96 w-full rounded-2xl" />
          ) : !area ? (
            <EmptyState icon={<Boxes />} title={t("ws.areas.noSuchArea")} />
          ) : (
            <>
              <header className="space-y-1">
                <h1 className="text-[1.75rem] leading-tight font-semibold tracking-[-0.02em]">{area.label}</h1>
                <p className="text-sm text-muted-foreground">
                  {t("ws.areas.types", { count: types.length })}
                  {area.core ? ` · ${t("ws.areas.coreHelp")}` : ""}
                </p>
              </header>
              {nodes.length ? (
                <div className="rounded-3xl border border-border bg-surface-subtle/40 p-4 md:p-6">
                  <Diagram id={`area-${area.id}`} nodes={nodes} edges={edges} label={t("ws.areas.flowLabel", { area: area.label })} />
                </div>
              ) : null}
              {outward.length ? (
                <section className="space-y-3">
                  <h2 className="text-[15px] font-semibold tracking-tight">{t("ws.areas.connections")}</h2>
                  <ul className="grid gap-x-8 sm:grid-cols-2">
                    {outward.slice(0, 16).map(({ from, link, other }) => {
                      const otherType = ontology.types.get(other)
                      const otherArea = ontology.areas.find((a) => a.types.includes(other))
                      return (
                        <li key={`${link.source}.${link.id}>${other}`} className="flex items-baseline gap-2 border-b border-border py-2 text-sm">
                          <span>{from.label}</span>
                          <span className="text-xs text-subtle-foreground">↔</span>
                          <Link to="/$orgId/$environmentId/records/$objectType" params={{ ...scope, objectType: other }} className="hover:underline">
                            {otherType?.label ?? other}
                          </Link>
                          {otherArea ? (
                            <Link to="/$orgId/$environmentId/areas/$areaId" params={{ ...scope, areaId: otherArea.id }} className="ms-auto text-2xs text-subtle-foreground hover:text-foreground">
                              {otherArea.label}
                            </Link>
                          ) : null}
                        </li>
                      )
                    })}
                  </ul>
                </section>
              ) : null}
              <section className="space-y-3">
                <h2 className="text-[15px] font-semibold tracking-tight">{t("ws.areas.allTypes")}</h2>
                <ul className="grid gap-x-8 sm:grid-cols-2 lg:grid-cols-3">
                  {types
                    .slice()
                    .sort((a, b) => a.pluralLabel.localeCompare(b.pluralLabel))
                    .map((ty) => (
                      <li key={ty.name} className="border-b border-border py-2.5">
                        <Link to="/$orgId/$environmentId/records/$objectType" params={{ ...scope, objectType: ty.name }} className="text-sm font-medium hover:underline">
                          {ty.pluralLabel}
                        </Link>
                        {typeBlurb(ty) ? <p className="truncate text-xs text-muted-foreground">{typeBlurb(ty)}</p> : null}
                      </li>
                    ))}
                </ul>
              </section>
            </>
          )}
        </Screen>
      </div>
    </>
  )
}
